import { useMemoizedFn } from 'ahooks';
import { produce } from 'immer';
import { find, isEmpty } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { AddIcon } from 'tdesign-icons-react';
import {
  Button,
  DialogPlugin,
  Link,
  MessagePlugin,
  Popconfirm,
  Space,
  Table,
  Tag,
} from 'tdesign-react';
import { connect } from 'react-redux';
import {
  flowGroupRuleOptions,
  flowGroupRuleOptionsScript,
  flowNodeTaskOptions,
  stencilTypeOptions,
  systemNodeTaskOptions,
} from '@/utils/constant';
import { isVoid, renderOptions } from '@/utils/common';
import {
  diffFlow,
  getComponentList,
  getFlowDataById,
  getScriptList,
  updateFlowStep,
} from '@/services/templateService';
import useTable from '@/hooks/useTable';
import FormModal from './FormModal';

export const getTaskOptions = (type) => {
  switch (type) {
    case 0:
      return flowGroupRuleOptionsScript;
    case 1:
      return flowNodeTaskOptions;
    case 2:
      return systemNodeTaskOptions;
    default:
      return [];
  }
};

const renderTaskOptions = ({ row }) => {
  const { type, task } = row;
  const options = getTaskOptions(type);
  return find(options, { value: task })?.label || '-';
};

function NodeList({ flowType, groupOptions }) {
  const [visible, setVisible] = useState(false);
  const [scriptOptions, setScriptOptions] = useState([]);
  const [componentOptions, setComponentOptions] = useState([]);
  const [searchParams] = useSearchParams();

  const autoNode = <Tag theme='success'>自动选择</Tag>;

  const id = searchParams.get('id');

  const columns = [
    {
      title: '流程序列',
      colKey: 'rowIndex',
      fixed: 'left',
      width: 100,
    },
    {
      title: '节点类型',
      colKey: 'type',
      cell: renderOptions,
      options: stencilTypeOptions,
      width: 120,
    },
    {
      title: '节点名称',
      colKey: 'name',
      width: 150,
    },
    {
      title: '节点任务',
      colKey: 'task',
      cell: (props) => (
        <Space size='small'>
          {renderTaskOptions(props)}
          {props.row.choiceType === 2 && autoNode}
        </Space>
      ),
      width: 200,
    },
    {
      title: '主机组',
      colKey: 'groupId',
      cell: renderOptions,
      options: groupOptions,
      width: 120,
    },
    {
      title: '主机组规则',
      colKey: 'groupRule',
      cell: renderOptions,
      options: flowGroupRuleOptions,
      width: 160,
    },
    {
      title: '文件路径',
      colKey: 'path',
      width: 200,
    },
    {
      title: '文件名修改为',
      colKey: 'fileName',
      width: 200,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      align: 'center',
      cell: ({ rowIndex }) => (
        <Space>
          <Link theme='primary' onClick={() => reOrder(rowIndex, rowIndex - 1)} hover='color'>
            上移
          </Link>
          <Link theme='primary' onClick={() => reOrder(rowIndex, rowIndex + 1)} hover='color'>
            下移
          </Link>
          <Popconfirm theme='danger' content='确定删除？' onConfirm={() => doDelete(rowIndex)}>
            <Link theme='danger' hover='color'>
              删除
            </Link>
          </Popconfirm>
        </Space>
      ),
      width: 150,
    },
  ];

  if (flowType === 1) {
    // k8s没有组概念
    columns.splice(4, 2);
  }

  const loadData = useMemoizedFn(async () => {
    const { data } = await getFlowDataById(id);
    setData(
      data.map((item, index) => ({
        ...item,
        rowIndex: index + 1,
        stencilTxt: item.stencilName,
      })),
    );
  });

  const doInitDict = useMemoizedFn(async () => {
    const { data } = await getScriptList({ startIndex: 1, pageSize: 99999 });
    setScriptOptions(
      data.items.map((item) => ({
        ...item,
        label: item.scriptName,
        value: item.id,
      })),
    );
    const { data: data2 } = await getComponentList({ startIndex: 1, pageSize: 99999 });
    setComponentOptions(
      data2.items.map((item) => ({
        ...item,
        label: item.name,
        value: item.id,
      })),
    );
  });

  const { setData, header, config, data, doSearch } = useTable({
    columns,
    refresh: null,
    columnController: null,
    search: <Title>节点信息</Title>,
    searchButton: null,
    onSearch: loadData,
    operators: (
      <Button variant='outline' theme='primary' icon={<AddIcon />} onClick={() => setVisible(true)}>
        新建节点
      </Button>
    ),
  });

  // 节点名称选项 依赖流程类型 + 节点类型（脚本）
  const tScriptOptions = useMemo(() => {
    if (flowType === 0) {
      // 虚拟机物理机
      return scriptOptions.filter((item) => item.scriptOriginal === 0);
    }
    if (flowType === 1) {
      // k8s
      return scriptOptions.filter((item) => item.scriptOriginal === 1);
    }
    return [];
  }, [scriptOptions, flowType]);

  // 节点名称选项 依赖流程类型 + 节点类型（组件）
  const tComponentOptions = useMemo(() => {
    if (!isVoid(flowType)) {
      return componentOptions.filter((item) => item.type === flowType);
    }
    return [];
  }, [componentOptions, flowType]);

  useEffect(() => {
    doInitDict();
    doSearch();
  }, [doInitDict, doSearch]);

  const doDelete = (index) => {
    const array = data.slice();
    array.splice(index, 1);
    setData(array);
  };

  const reOrder = (currentIndex, nextIndex) => {
    const array = data.slice();
    if (nextIndex > -1 && nextIndex < array.length) {
      [array[currentIndex], array[nextIndex]] = [array[nextIndex], array[currentIndex]];
      setData(array);
    }
  };

  const handleClose = (value) => {
    setVisible(false);
    if (value) {
      setData(
        produce(data, (draft) => {
          draft.push(value);
        }),
      );
    }
  };

  const handleSubmit = async () => {
    const params = {
      flowId: id,
      stepInfoList: data.map((item, index) => ({
        ...item,
        num: index + 1,
        id: 0,
        taskTxt: find(getTaskOptions(item.type), { value: item.task }).label,
        typeTxt: find(stencilTypeOptions, { value: item.type }).label,
      })),
    };

    const { data: diffData } = await diffFlow(params);
    const dialog = DialogPlugin.confirm({
      header: '预览',
      placement: 'center',
      width: '80%',
      body: isEmpty(diffData)
        ? '节点无变化'
        : diffData?.map((item, index) => <p key={index}>{item.content}</p>),
      onClose: () => {
        dialog.destroy();
      },
      onConfirm: async () => {
        try {
          dialog.update({
            confirmBtn: { loading: true, content: '确认' },
          });
          await updateFlowStep(params);
          MessagePlugin.success('操作成功！');
          dialog.destroy();
        } catch (error) {
          console.trace(error);
          throw error;
        } finally {
          dialog.update({
            confirmBtn: { loading: false, content: '确认' },
          });
        }
      },
    });
  };

  return (
    <>
      {header}
      <Table {...config} />
      <Footer>
        <Button onClick={handleSubmit}>提交预览</Button>
      </Footer>
      <FormModal
        componentOptions={tComponentOptions}
        scriptOptions={tScriptOptions}
        visible={visible}
        handleClose={handleClose}
        flowType={flowType}
      />
    </>
  );
}

export default connect((state) => ({
  groupOptions: state.template.currentFlowGroups.map((item) => ({
    label: item.name,
    value: item.id,
  })),
}))(NodeList);

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--td-text-color-primary);
  padding-left: 8px;
  display: flex;
  align-items: center;
  height: 32px;
`;
